import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  alpha,
  useTheme,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import apiService from '../../../services/apiService';

// واجهة الرسم
interface Fee {
  id?: string;
  name: string;
  type: 'fixed' | 'percentage' | 'tax';
  value: number;
}

// واجهة مجموعة الرسوم
interface FeeGroup {
  id?: string;
  _id?: string;  // إضافة دعم لـ _id من MongoDB
  name: string;
  description?: string;
  fees: Fee[];
  createdAt?: string;
  updatedAt?: string;
}

const FeeGroupsList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  
  // حالة القائمة
  const [feeGroups, setFeeGroups] = useState<FeeGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // تفاصيل المجموعة
  const [selectedFeeGroup, setSelectedFeeGroup] = useState<FeeGroup | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState<boolean>(false);
  
  // حوار الحذف
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [feeGroupToDelete, setFeeGroupToDelete] = useState<FeeGroup | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  
  // الصفحات
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  
  // جلب قائمة مجموعات الرسوم
  const fetchFeeGroups = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.get<any[]>('/finance/fee-groups');
      
      // تسجيل معلومات عن البيانات المستلمة للتشخيص
      console.log('بيانات مجموعات الرسوم المستلمة:', response);
      
      // التحقق من صحة البيانات
      if (Array.isArray(response)) {
        // تحويل البيانات للتأكد من توافقها مع واجهة FeeGroup
        const normalizedFeeGroups = response.map(group => {
          // تحقق من وجود _id (المعرف في MongoDB) وضمان وجود id للاستخدام في الواجهة
          if (group._id && !group.id) {
            return { ...group, id: group._id };
          }
          return group;
        });
        
        console.log('بيانات مجموعات الرسوم بعد التحويل:', normalizedFeeGroups);
        setFeeGroups(normalizedFeeGroups);
      } else {
        console.error('تنسيق استجابة API غير صالح:', response);
        setError('تنسيق البيانات المستلمة من الخادم غير صالح');
      }
      setLoading(false);
    } catch (error: any) {
      console.error('خطأ في جلب مجموعات الرسوم:', error);
      setError(error.response?.data?.message || 'حدث خطأ في جلب مجموعات الرسوم');
      setLoading(false);
    }
  };
  
  // تحميل البيانات عند فتح الصفحة
  useEffect(() => {
    fetchFeeGroups();
  }, []);
  
  // حذف مجموعة رسوم
  const handleDeleteFeeGroup = async () => {
    if (!feeGroupToDelete) return;
    
    setDeleteLoading(true);
    
    try {
      await apiService.delete(`/finance/fee-groups/${feeGroupToDelete.id}`);
      
      // تحديث القائمة بعد الحذف
      setFeeGroups(prevGroups => prevGroups.filter(group => group.id !== feeGroupToDelete.id));
      
      // إغلاق حوار الحذف
      setDeleteDialogOpen(false);
      setFeeGroupToDelete(null);
      setDeleteLoading(false);
    } catch (error: any) {
      console.error('خطأ في حذف مجموعة الرسوم:', error);
      setError(error.response?.data?.message || 'حدث خطأ في حذف مجموعة الرسوم');
      setDeleteLoading(false);
    }
  };
  
  // فتح حوار الحذف
  const openDeleteDialog = (feeGroup: FeeGroup) => {
    setFeeGroupToDelete(feeGroup);
    setDeleteDialogOpen(true);
  };
  
  // فتح حوار التفاصيل
  const openDetailsDialog = (feeGroup: FeeGroup) => {
    setSelectedFeeGroup(feeGroup);
    setDetailsDialogOpen(true);
  };
  
  // التصفية بالبحث
  const filteredFeeGroups = feeGroups.filter(group => 
    group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (group.description && group.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  // إعداد الصفحات
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // عرض نص نوع الرسم بالعربية
  const getFeeTypeText = (type: 'fixed' | 'percentage' | 'tax') => {
    switch (type) {
      case 'fixed': return 'ثابت';
      case 'percentage': return 'نسبة مئوية';
      case 'tax': return 'ضريبة';
      default: return '';
    }
  };
  
  // عرض قيمة الرسم بطريقة مناسبة حسب النوع
  const getFeeValueText = (fee: Fee) => {
    switch (fee.type) {
      case 'fixed': return `${fee.value} ريال`;
      case 'percentage': return `${fee.value}%`;
      case 'tax': return `${fee.value}%`;
      default: return '';
    }
  };
  
  // تنسيق التاريخ
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* رأس الصفحة */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            مجموعات الرسوم
          </Typography>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/finance/fee-groups/create')}
          >
            إنشاء مجموعة رسوم جديدة
          </Button>
        </Box>
        
        {/* عرض الأخطاء */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        {/* شريط البحث */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="البحث في مجموعات الرسوم..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
          />
        </Box>
        
        {/* حالة التحميل */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
          </Box>
        ) : filteredFeeGroups.length === 0 ? (
          // حالة عدم وجود نتائج
          <Box sx={{ 
            p: 4, 
            textAlign: 'center', 
            borderRadius: 1, 
            bgcolor: alpha(theme.palette.action.hover, 0.5)
          }}>
            <InfoIcon color="disabled" sx={{ fontSize: 48, mb: 2, opacity: 0.7 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              لا توجد مجموعات رسوم
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {searchTerm 
                ? 'لا توجد نتائج تطابق عبارة البحث. جرب بحثًا آخر أو قم بإنشاء مجموعة رسوم جديدة.'
                : 'لم يتم إنشاء أي مجموعات رسوم بعد. انقر على "إنشاء مجموعة رسوم جديدة" للبدء.'}
            </Typography>
            
            {searchTerm && (
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={() => setSearchTerm('')}
                sx={{ mt: 1 }}
              >
                مسح البحث
              </Button>
            )}
          </Box>
        ) : (
          // عرض الجدول
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>اسم المجموعة</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>الوصف</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>عدد الرسوم</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>تاريخ الإنشاء</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>إجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFeeGroups
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((feeGroup) => (
                      <TableRow key={feeGroup.id} hover>
                        <TableCell>{feeGroup.name}</TableCell>
                        <TableCell>
                          {feeGroup.description 
                            ? feeGroup.description.length > 50 
                              ? `${feeGroup.description.substring(0, 50)}...` 
                              : feeGroup.description
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${feeGroup.fees.length} رسوم`} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatDate(feeGroup.createdAt)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="عرض التفاصيل">
                            <IconButton 
                              size="small" 
                              onClick={() => openDetailsDialog(feeGroup)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="تعديل">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                // تسجيل معلومات تشخيصية للمراقبة فقط
                                console.log('بيانات المجموعة عند النقر على زر التعديل:', {
                                  feeGroup: feeGroup,
                                  id: feeGroup.id,
                                  _id: feeGroup._id,
                                  type: typeof feeGroup.id
                                });
                                
                                // التعامل مع المعرف - قد يكون في id أو _id
                                const groupId = feeGroup.id || feeGroup._id;
                                
                                // معالجة حالة undefined أو null بشكل محدد
                                if (groupId === undefined || groupId === null) {
                                  console.error('معرف المجموعة غير موجود (undefined/null)');
                                  setError('يرجى تحديث الصفحة وإعادة المحاولة، تعذر الوصول إلى بيانات المجموعة بشكل صحيح');
                                  return;
                                }
                                
                                // الانتقال إلى صفحة التعديل باستخدام المعرف المناسب
                                navigate(`/finance/fee-groups/edit/${groupId}`);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="حذف">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => openDeleteDialog(feeGroup)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredFeeGroups.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="عدد العناصر في الصفحة:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
            />
          </>
        )}
      </Paper>
      
      {/* حوار تفاصيل مجموعة الرسوم */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Typography variant="h6">
            تفاصيل مجموعة الرسوم: {selectedFeeGroup?.name}
          </Typography>
        </DialogTitle>
        
        <DialogContent dividers>
          {selectedFeeGroup && (
            <Box>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    معلومات المجموعة
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        الاسم:
                      </Typography>
                      <Typography variant="body1">
                        {selectedFeeGroup.name}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        تاريخ الإنشاء:
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(selectedFeeGroup.createdAt)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="body2" color="text.secondary">
                        الوصف:
                      </Typography>
                      <Typography variant="body1">
                        {selectedFeeGroup.description || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    الرسوم في المجموعة
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  {selectedFeeGroup.fees.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>اسم الرسم</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>النوع</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>القيمة</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedFeeGroup.fees.map((fee, index) => (
                            <TableRow key={index}>
                              <TableCell>{fee.name}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={getFeeTypeText(fee.type)}
                                  size="small"
                                  color={
                                    fee.type === 'fixed' ? 'primary' :
                                    fee.type === 'percentage' ? 'secondary' : 'default'
                                  }
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>{getFeeValueText(fee)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ 
                      p: 2, 
                      textAlign: 'center', 
                      borderRadius: 1, 
                      bgcolor: alpha(theme.palette.action.hover, 0.5)
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        لا توجد رسوم في هذه المجموعة.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => setDetailsDialogOpen(false)} 
            color="inherit"
          >
            إغلاق
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (selectedFeeGroup) {
                // تسجيل معلومات تشخيصية للمراقبة فقط
                console.log('بيانات المجموعة عند النقر على زر التعديل في الحوار:', {
                  id: selectedFeeGroup.id,
                  _id: selectedFeeGroup._id,
                  type: typeof selectedFeeGroup.id,
                  name: selectedFeeGroup.name
                });
                
                // التعامل مع المعرف - قد يكون في id أو _id
                const groupId = selectedFeeGroup.id || selectedFeeGroup._id;
                
                // معالجة حالة undefined أو null بشكل محدد
                if (groupId === undefined || groupId === null) {
                  console.error('معرف المجموعة في الحوار غير موجود (undefined/null)');
                  setDetailsDialogOpen(false);
                  setError('يرجى تحديث الصفحة وإعادة المحاولة، تعذر الوصول إلى بيانات المجموعة بشكل صحيح');
                  return;
                }
                
                // الانتقال إلى صفحة التعديل
                setDetailsDialogOpen(false);
                navigate(`/finance/fee-groups/edit/${groupId}`);
              }
            }}
            startIcon={<EditIcon />}
          >
            تعديل
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* حوار حذف مجموعة رسوم */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          حذف مجموعة رسوم
        </DialogTitle>
        
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من رغبتك في حذف مجموعة الرسوم "{feeGroupToDelete?.name}"؟ لا يمكن التراجع عن هذه العملية.
          </DialogContentText>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            color="inherit"
            disabled={deleteLoading}
          >
            إلغاء
          </Button>
          <Button 
            onClick={handleDeleteFeeGroup} 
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            حذف
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeeGroupsList;